import React from 'react';

export default function KpiStrip({
  borewellId = '',
  todayConsumption = 0,
  todayDelta = null,
  monthConsumption = 0,
  monthDelta = null,
  cumulativePumpHours = 0,
  todayPumpHours = 0,
  flowDuration = '',
  totalizer = 0,
  forwardTotalizer = 0,
  reverseTotalizer = 0,
  unit = 'm³',
}) {
  const formatNumber = (val, decimals = 2) => {
    if (isNaN(val) || val === null || val === undefined) return '0.00';
    return Number(val).toLocaleString('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatHoursAndMins = (decimalHours) => {
    if (isNaN(decimalHours) || decimalHours === null || decimalHours === undefined) {
      return { hrs: 0, mins: 0, text: '0 hrs 0 mins' };
    }
    const totalMinutes = Math.round(Number(decimalHours) * 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return {
      hrs,
      mins,
      text: hrs > 0 ? (mins > 0 ? `${hrs} hrs ${mins} mins` : `${hrs} hrs`) : `${mins} mins`,
    };
  };

  const pumpDuration = formatHoursAndMins(cumulativePumpHours);
  const todayDuration = formatHoursAndMins(todayPumpHours);

  const hasBorewellId = Boolean(borewellId && String(borewellId).trim() !== '' && String(borewellId).trim() !== '-');

  return (
    <div className="kpis" style={{ marginBottom: '22px' }}>
      {/* 1. Today */}
      <div className="kpi">
        <div className="k-lab">Today’s consumption</div>
        <div className="k-val">
          <span>{formatNumber(todayConsumption, 2)}</span>
          <u>{unit}</u>
        </div>
        <div className={`delta ${todayDelta !== null ? (todayDelta >= 0 ? 'up' : 'down') : ''}`}>
          {todayDelta !== null ? (
            <>
              {todayDelta >= 0 ? '▲ ' : '▼ '}
              {Math.abs(todayDelta).toFixed(2)}% vs yesterday
            </>
          ) : (
            'Live cumulative reading'
          )}
        </div>
      </div>

      {/* 2. This Month */}
      <div className="kpi">
        <div className="k-lab">This month</div>
        <div className="k-val">
          <span>{formatNumber(monthConsumption, 2)}</span>
          <u>{unit}</u>
        </div>
        <div className={`delta ${monthDelta !== null ? (monthDelta >= 0 ? 'up' : 'down') : ''}`}>
          {monthDelta !== null ? (
            <>
              {monthDelta >= 0 ? '▲ ' : '▼ '}
              {Math.abs(monthDelta).toFixed(2)}% vs last month
            </>
          ) : (
            'Net totalizer'
          )}
        </div>
      </div>

      {/* 3. Cumulative Pump Working Hours */}
      <div className="kpi">
        <div className="k-lab">Cumulative pump hours</div>
        <div className="k-val" style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
          {pumpDuration.hrs > 0 ? (
            <>
              <span>{Number(pumpDuration.hrs).toLocaleString('en-IN')}</span>
              <u>hrs</u>
              {pumpDuration.mins > 0 && (
                <>
                  <span style={{ marginLeft: '6px' }}>{pumpDuration.mins}</span>
                  <u>mins</u>
                </>
              )}
            </>
          ) : (
            <>
              <span>{pumpDuration.mins}</span>
              <u>mins</u>
            </>
          )}
        </div>
      </div>

      {/* 4. Totalizer (Net, Forward, Reverse) */}
      <div className="kpi">
        <div className="k-lab">Totalizer</div>
        <div className="k-val">
          <span>{formatNumber(totalizer, 2)}</span>
          <u>{unit}</u>
        </div>
        <div className="delta" style={{ fontSize: '11px', color: '#556987', fontWeight: 600 }}>
          <span>Fwd: <strong>{formatNumber(forwardTotalizer || totalizer, 2)}</strong></span>
          <span style={{ margin: '0 4px', color: '#cbd5e1' }}>|</span>
          <span>Rev: <strong>{formatNumber(reverseTotalizer, 2)}</strong></span>
        </div>
      </div>

      {/* 5. Bore-well ID */}
      <div className="kpi">
        <div className="k-lab" style={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title="Bore-well ID (Provided by CGWA)">
          Bore-well ID
        </div>
        <div
          className="k-val"
          style={{
            fontSize: hasBorewellId ? '18px' : '30px',
            lineHeight: '1.2',
            minHeight: '38px',
            display: 'flex',
            alignItems: 'center',
            wordBreak: 'break-word',
          }}
        >
          <span>{hasBorewellId ? borewellId : '—'}</span>
        </div>
        <div className="delta" style={{ fontSize: '11px', color: '#556987' }}>
          Provided by CGWA
        </div>
      </div>
    </div>
  );
}
